import {
  httpsCallable,
} from 'firebase/functions';
import { functions } from '../firebase/config';
import { AuthService } from './authService';

// Keep in sync with functions/index.js (UID_MAX_PER_WINDOW / WINDOW_MS)
export const FEEDBACK_RATE_LIMIT = {
  maxSubmissions: 1,
  windowMs: 60 * 60 * 1000, // 1 hour
};

const CLIENT_COOLDOWN_MS = 30 * 1000;
const CLIENT_COOLDOWN_KEY = 'stabletrust:feedbackLastSubmitAt';

export class FeedbackRateLimitError extends Error {
  /**
   * @param {number} retryAfterMs
   */
  constructor(retryAfterMs = FEEDBACK_RATE_LIMIT.windowMs) {
    const ms = Math.max(1000, retryAfterMs);
    const minutes = Math.ceil(ms / 1000 / 60);
    const seconds = Math.ceil(ms / 1000);
    const when =
      seconds < 90
        ? `${seconds} second${seconds === 1 ? '' : 's'}`
        : `${minutes} minute${minutes === 1 ? '' : 's'}`;
    super(`Too many feedback submissions. Please try again in about ${when}.`);
    this.name = 'FeedbackRateLimitError';
    this.retryAfterMs = ms;
  }
}

function getClientCooldownRemainingMs() {
  try {
    const raw = localStorage.getItem(CLIENT_COOLDOWN_KEY);
    if (!raw) return 0;
    const last = Number(raw);
    if (!Number.isFinite(last)) return 0;
    return Math.max(0, CLIENT_COOLDOWN_MS - (Date.now() - last));
  } catch {
    return 0;
  }
}

function markClientCooldown() {
  try {
    localStorage.setItem(CLIENT_COOLDOWN_KEY, String(Date.now()));
  } catch {
    // ignore quota / private mode
  }
}

export class FeedbackService {
  /**
   * Submit user feedback or bug report via Cloud Function (IP + UID rate limits).
   * @param {Object} feedbackData
   * @returns {Promise<string>} Document ID of the created feedback
   */
  static async submitFeedback(feedbackData) {
    try {
      const cooldownMs = getClientCooldownRemainingMs();
      if (cooldownMs > 0) {
        throw new FeedbackRateLimitError(cooldownMs);
      }

      if (!AuthService.isAuthenticated()) {
        await AuthService.signInAnonymously();
      }

      const user = AuthService.getCurrentUser();
      if (!user?.uid) {
        throw new Error('Could not authenticate. Please try again.');
      }

      const submit = httpsCallable(functions, 'submitFeedback');
      const result = await submit({
        type: feedbackData.type || 'feedback',
        subject: feedbackData.subject,
        message: feedbackData.message,
        email: feedbackData.email,
        userAddress: feedbackData.userAddress || null,
        browserInfo: feedbackData.browserInfo || null,
        deviceInfo: feedbackData.deviceInfo || null,
        url: feedbackData.url || window.location.href,
        chainId: feedbackData.chainId || null,
        walletType: feedbackData.walletType || null,
      });

      markClientCooldown();
      return result?.data?.id || null;
    } catch (error) {
      if (error instanceof FeedbackRateLimitError) {
        throw error;
      }

      const code = error?.code || '';
      const details = error?.details;
      const retryAfterMs =
        (details && details.retryAfterMs) || FEEDBACK_RATE_LIMIT.windowMs;

      // Callable HttpsError codes are prefixed: functions/resource-exhausted
      if (
        code === 'functions/resource-exhausted' ||
        code === 'resource-exhausted'
      ) {
        throw new FeedbackRateLimitError(retryAfterMs);
      }

      console.error('Error submitting feedback:', error);
      throw error;
    }
  }

  /**
   * Get browser and device information for bug reports
   * @returns {Object} Browser and device info
   */
  static getBrowserInfo() {
    const ua = navigator.userAgent;
    const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);

    return {
      userAgent: ua,
      platform: navigator.platform,
      language: navigator.language,
      isMobile: isMobile,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };
  }
}
